import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { groupCompanies, visibleAssociateCompanies } from '../../mocks/companies';

const groupLinks = groupCompanies.map(c => ({
  name: c.name,
  path: `/company/${c.slug}`
}));

const associateLinks = visibleAssociateCompanies.map(c => ({
  name: c.name,
  path: `/company/${c.slug}`
}));

function NavDropdown({
  label,
  items,
}: {
  label: string;
  items: { name: string; path: string }[];
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleMouseEnter = () => {
    if (window.innerWidth >= 1024) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      setOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (window.innerWidth >= 1024) {
      timeoutRef.current = setTimeout(() => setOpen(false), 150);
    }
  };

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-3 py-2 text-[12px] tracking-[0.08em] font-medium text-gray-400 hover:text-gray-900 transition-colors duration-200 cursor-pointer whitespace-nowrap"
      >
        {label}
        <i className={`ri-arrow-down-s-line text-[14px] transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      <div
        className={`absolute top-full left-0 mt-1 min-w-[220px] bg-white border border-gray-100 shadow-[0_8px_24px_rgba(0,0,0,0.06)] rounded-md py-1.5 z-50 transition-all duration-200 ${
          open ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-1 pointer-events-none'
        }`}
      >
        {items.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className="flex items-center px-4 py-2.5 text-[12px] tracking-[0.05em] font-medium text-gray-500 hover:text-gray-900 hover:bg-gray-50/80 transition-colors duration-150 cursor-pointer whitespace-nowrap"
            onClick={() => setOpen(false)}
          >
            {item.name}
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const LanguageToggle = () => (
    <div className="flex items-center gap-1 bg-gray-50 p-1 rounded-full border border-gray-100">
      <button
        onClick={() => i18n.changeLanguage('en')}
        className={`px-3 py-1 text-[10px] font-bold rounded-full transition-all duration-200 ${
          i18n.language === 'en' ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-400 hover:text-gray-600'
        }`}
      >
        EN
      </button>
      <button
        onClick={() => i18n.changeLanguage('ja')}
        className={`px-3 py-1 text-[10px] font-bold rounded-full transition-all duration-200 ${
          i18n.language === 'ja' ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-400 hover:text-gray-600'
        }`}
      >
        JP
      </button>
    </div>
  );

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled || mobileOpen
            ? 'bg-white/95 backdrop-blur-md shadow-[0_1px_0_0_rgba(0,0,0,0.06)]'
            : 'bg-white'
        }`}
      >
        <div className="w-[90%] mx-auto flex items-center justify-between h-[76px]">
          <Link to="/" className="flex items-center gap-3 cursor-pointer shrink-0">
            <span
              className="text-[18px] md:text-[20px] font-semibold tracking-[0.12em] text-gray-900 leading-none"
              style={{ fontFamily: "'Josefin Sans', sans-serif" }}
            >
              SWELL GROUP
            </span>
          </Link>

          <div className="flex items-center gap-4">
            <nav className="hidden lg:flex items-center gap-6">
              <NavDropdown label="GROUP" items={groupLinks} />
              <NavDropdown label="ASSOCIATES" items={associateLinks} />
              {/* COMPANY INFORMATION ボタンと useNavigate を削除しました */}
              <div className="h-4 w-[1px] bg-gray-200 mx-2" />
              <LanguageToggle />
            </nav>

            <div className="lg:hidden flex items-center">
              <LanguageToggle />
            </div>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden w-8 h-8 flex items-center justify-center cursor-pointer"
              aria-label="Toggle menu"
            >
              <div className="flex flex-col gap-[5px]">
                <span className={`block w-5 h-[1.5px] bg-gray-900 transition-all duration-300 ${mobileOpen ? 'rotate-45 translate-y-[6.5px]' : ''}`} />
                <span className={`block w-5 h-[1.5px] bg-gray-900 transition-all duration-300 ${mobileOpen ? 'opacity-0' : ''}`} />
                <span className={`block w-5 h-[1.5px] bg-gray-900 transition-all duration-300 ${mobileOpen ? '-rotate-45 -translate-y-[6.5px]' : ''}`} />
              </div>
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 py-6 overflow-y-auto max-h-[calc(100vh-76px)]">
            <div className="w-[90%] mx-auto flex flex-col gap-1">
              <Link to="/" className={`py-3 text-[13px] tracking-[0.08em] font-medium ${location.pathname === '/' ? 'text-gray-900' : 'text-gray-400'}`}>HOME</Link>
              <div className="py-2 text-[10px] tracking-[0.15em] text-gray-300 font-medium uppercase border-b border-gray-50 mb-1">Group</div>
              {groupLinks.map((item) => (
                <Link key={item.path} to={item.path} className="flex items-center py-2.5 pl-3 text-[13px] tracking-[0.05em] text-gray-500">{item.name}</Link>
              ))}
              <div className="py-2 mt-4 text-[10px] tracking-[0.15em] text-gray-300 font-medium uppercase border-b border-gray-50 mb-1">Associates</div>
              {associateLinks.map((item) => (
                <Link key={item.path} to={item.path} className="flex items-center py-2.5 pl-3 text-[13px] tracking-[0.05em] text-gray-500">{item.name}</Link>
              ))}
              {/* スマホ用メニューからも COMPANY INFORMATION を削除 */}
            </div>
          </div>
        )}
      </header>
      <div className="h-[76px]" />
    </>
  );
}
